import { useMemo } from "react";
import useAcquisitionProviderFacade from "../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade";
import useBriteVerify from "../../hooks/useBriteVerify";
import { useAcquisitionContext } from "../../providers/AcquisitionProvider/AcquisitionProvider";
import { IPersonalInfoErrors, TPersonalInfoError } from "../../providers/AcquisitionProvider/models";
import { useFeatureFlags } from "../../providers/FeatureFlagProvider/FeatureFlagProvider";
import { usePushdownContext } from "../../providers/Pushdown/PushdownProvider";
import { AcquisitionFlow, getAcquisitionFlow, isDateValid } from '../../utils/acquisition-utils';
import { isValidEmailFormat, regexValidation } from "../../utils/cob-utils";
import { cleanNumericInput } from "../../utils/income-utils";
import { hasDefaultComponent } from "../../utils/pageModelUtils";
import SessionStorage from "../../utils/SessionStorage";
import sortPushdownErrors from "./sortPushdownErrors";
import { IFieldName, TErrorType } from "../../providers/Pushdown/util/getPushdownError";

// This code explicitly has its own tests.
// Migrating the tests into useHandleErrorPushdown's suite would be ideal
// For now, exporting for its tests and using in the hook, as it should be a stable ref
export const getInvalidPersonalInformationFields = (
    errorObject: IPersonalInfoErrors,
    errorOverrides?: Partial<IPersonalInfoErrors>,
): Array<[keyof IPersonalInfoErrors, TErrorType]> => {
    const errorMap = new Map(
        Object.entries(errorObject)
    ) as Map<
        keyof IPersonalInfoErrors,
        TPersonalInfoError
    >;
    if (errorOverrides) {
        for (const [key, value] of Object.entries(errorOverrides)) {
            if (key && value) {
                errorMap.set(key as keyof IPersonalInfoErrors, value);
            }
        }
    }
    errorMap.forEach((errorType, key): void => {
        if (errorType === 'None') {
            errorMap.delete(key);
        }
    });

    return Array.from(errorMap.entries()) as [keyof IPersonalInfoErrors, TErrorType][];
};

const useHandleErrorPushdown = (
    isSamePersonCheck?: (isSecondPage: boolean) => Promise<boolean>
): {
    getInvalidPersonalInformationFields: (errorObject: IPersonalInfoErrors, errorOverrides?: Partial<IPersonalInfoErrors>) => Array<[keyof IPersonalInfoErrors, TErrorType]>,
    handleErrorPushdown: (validatePersonalInformation: boolean, validationErrorMessage?: string, homeAddressPrimary?: boolean) => Promise<void>,
} => {

    const { showPushdown } = usePushdownContext();
    const { getFeatureFlags } = useFeatureFlags();

    const {
        isAuthorisedUserSectionValid,
        isMonthlyIncomeValid,
        checkIsCreditProtectionValid,
        ssnInfo,
        isSsnInfoValid,
        personalInfoForm,
        authorizedUserInfo,
        selectedCardId,
        isEsignShowing,
        isEsignLinkValid,
        personalInfoErrors,
        homeAddressValidateOnSubmit,
        setPersonalInfoForm
    } = useAcquisitionContext();
    const {
        getPersonalInformation,
        getIsPhoneNumberValid
    } = useAcquisitionProviderFacade();

    const { emailValidationApi, phoneValidationApi } = useBriteVerify();

    const {
        regex,
        isAuthor,
        isAdobeMode,
        useEmailApi,
        usePhoneApi,
    } = useMemo(() => ({
        regex: regexValidation(),
        isAuthor: ['4502', '3000'].some((entry) => window.location.port.includes(entry)),
        isAdobeMode: SessionStorage.contains('adobeMode'),
        useEmailApi: SessionStorage.contains('useEmailApi'),
        usePhoneApi: SessionStorage.contains('usePhoneApi'),
    }), []);

    const validateMailingAddress = (): Partial<IPersonalInfoErrors> => {
        const errorOverrides: Partial<IPersonalInfoErrors> = {};
        if (
            !hasDefaultComponent('cob-corp-acquisition/components/creditonebank/personal-information-form')
        ) {
            return {}
            
        } else {
            // required mailing address fields required for full form validation
            if (!personalInfoForm?.mailingAddress?.value) {
                errorOverrides.MailingAddressLine1 = 'RequiredField';
            }
            if (!personalInfoForm?.mailingCity?.value) {
                errorOverrides.MailingCity = 'RequiredField';
            }
            if (!personalInfoForm?.mailingState?.value) {
                errorOverrides.MailingState = 'RequiredField';
            }
            if (!personalInfoForm?.mailingZip?.value) {
                errorOverrides.MailingZipCode = 'RequiredField';
            }

            if (
                personalInfoForm?.mailingAddress?.value &&
                personalInfoForm?.mailingAddress?.value ===
                personalInfoForm?.mailingAptUnit?.value
            ) {
                // cannot be RequiredField to differentiate from other errors since fields are cleared.
                errorOverrides.AddrAndAptNumMatch = 'NotValid';
                setPersonalInfoForm(personalInfoForm => ({
                    ...personalInfoForm,
                    mailingAddress: { value: '', valid: false },
                    mailingAptUnit: { value: '', valid: true },
                    mailingCity: { value: '', valid: false },
                    mailingState: { value: '', valid: false },
                    mailingZip: { value: '', valid: false },
                }));
            }
        }
        return errorOverrides;
    }

    const validateHomeAddress = (validationErrorMessage?: string): Partial<IPersonalInfoErrors> => {
        if (
            !homeAddressValidateOnSubmit && validationErrorMessage !== 'loqate home error'
        ) {
            return {};
        }

        const errorOverrides: Partial<IPersonalInfoErrors> = {};
        if (!personalInfoForm.homeAddress.value) {
            errorOverrides.HomeAddressLine1 = 'RequiredField';
        }
        if (!personalInfoForm.homeCity.value) {
            errorOverrides.HomeCity = 'RequiredField';
        }
        if (!personalInfoForm.homeState.value) {
            errorOverrides.HomeState = 'RequiredField';
        }
        if (!personalInfoForm.homeZip.value) {
            errorOverrides.HomeZipCode = 'RequiredField';
        }

        if (
            personalInfoForm?.homeAddress?.value &&
            personalInfoForm.homeAddress.value ===
            personalInfoForm.homeAptUnit.value
        ) {
            // cannot be RequiredField to differentiate from other errors since fields are cleared.
            errorOverrides.HomeAddrAndAptNumMatch = 'NotValid';
            setPersonalInfoForm(personalInfoForm => ({
                ...personalInfoForm,
                homeAddress: { value: '', valid: false },
                homeAptUnit: { value: '', valid: false },
                homeCity: { value: '', valid: false },
                homeState: { value: '', valid: false },
                homeZip: { value: '', valid: false },
            }));
        } else {
            // ensure fields display inline error if empty.
            setPersonalInfoForm(prevInfo => ({
                ...prevInfo,
                homeAddress: {
                    value: prevInfo.homeAddress.value,
                    valid:
                        prevInfo.homeAddress.valid === false
                            ? false
                            : Boolean(
                                prevInfo.homeAddress.value,
                            ),
                },
                homeCity: {
                    value: prevInfo.homeCity.value,
                    valid:
                        prevInfo.homeCity.valid === false
                            ? false
                            : Boolean(
                                prevInfo.homeCity.value,
                            ),
                },
                homeState: {
                    value: prevInfo.homeState.value,
                    valid:
                        prevInfo.homeState.valid === false
                            ? false
                            : Boolean(
                                prevInfo.homeState.value,
                            ),
                },
                homeZip: {
                    value: prevInfo.homeZip.value,
                    valid:
                        prevInfo.homeZip.valid === false
                            ? false
                            : Boolean(
                                prevInfo.homeZip.value,
                            ),
                },
            }));
        }
        return errorOverrides;
    }

    const fullValidateRegex = (key: keyof typeof regex['allow' | 'disallow'], value: string): boolean => {
        if (!value) {
            return false
        }
        return (regex.allow[key].test(value) && !regex.disallow[key].test(value))
    };

    const handleErrorPushdown = async (isSecondPage: boolean, validationErrorMessage?: string, homeAddressPrimary?: boolean): Promise<void> => {
        let fieldErrors: [IFieldName, string][] = [];
        const acquisitionData = getPersonalInformation();

        if (!isSecondPage) {
            const errorOverrides: Partial<IPersonalInfoErrors> = {
                ...validateMailingAddress(),
                ...validateHomeAddress(validationErrorMessage),
            };


            if (hasDefaultComponent('cob-corp-acquisition/components/creditonebank/personal-information-form')) {
                if (acquisitionData.firstName.value && !fullValidateRegex('first_name', acquisitionData.firstName.value)) {
                    errorOverrides.FirstName = 'NotValid';
                }
                if (acquisitionData.lastName.value && !fullValidateRegex('last_name', acquisitionData.lastName.value)) {
                    errorOverrides.LastName = 'NotValid';
                }
            } else {
                errorOverrides.FirstName = 'None';
                errorOverrides.LastName = 'None';
            }

            fieldErrors = fieldErrors.concat(
                getInvalidPersonalInformationFields(
                    personalInfoErrors,
                    errorOverrides,
                ).filter(
                    error => !(error[0] === 'MonthlyIncome' && personalInfoForm.monthlyIncome.valid)
                )
            )
        }
        if (
            hasDefaultComponent('cob-corp-acquisition/components/creditonebank/plastic-card-design')
        ) {
            if (!selectedCardId) {
                fieldErrors.push(['SelectedCardId', 'RequiredField']);
            }
        }

        if (
            hasDefaultComponent(
                'cob-corp-acquisition/components/creditonebank/ssn-module',
            ) &&
            (!isSsnInfoValid || ssnInfo?.length < 9)
        ) {
            if (!ssnInfo) {
                fieldErrors.push(['Ssn', 'RequiredField']);
            }
            if (ssnInfo && ssnInfo.length < 9) {
                fieldErrors.push(['Ssn', 'NotValid']);
            }
        }

// PHONE VALIDATION START
        const phoneNumber = acquisitionData?.phoneNumber?.value?.replace(/[^0-9]/g, '');;
        if (!getIsPhoneNumberValid() && !(fieldErrors.some((fieldError) => fieldError[0] === 'PrimaryPhoneNumber'))) {
            if (!phoneNumber) {
                fieldErrors.push(['PrimaryPhoneNumber', 'RequiredValue']);            
            } else if (phoneNumber.length !== 10) {
                fieldErrors.push(['PrimaryPhoneNumber', 'NotValid']);
            } else if (phoneNumber && (
                (!isAuthor && !isAdobeMode)
                || (isAdobeMode && usePhoneApi)
            ) && !(await phoneValidationApi(phoneNumber))) {
                fieldErrors.push(['PrimaryPhoneNumber', 'ThirdParty']);
            }
        }
        if (hasDefaultComponent('cob-corp-acquisition/components/creditonebank/phone-number-and-income')) {            
            if (isMonthlyIncomeValid && !acquisitionData?.monthlyIncome.value) {
                fieldErrors.push(['MonthlyIncome', 'RequiredField']);
            } else if (!isMonthlyIncomeValid || cleanNumericInput(acquisitionData.monthlyIncome.value).length < 3) {
                fieldErrors.push(['MonthlyIncome', 'NotValid']);            
            }
        }
// PHONE VALIDATION END

// EMAIL VALIDATION START
        const emailValue = acquisitionData?.emailAddress?.value;
        const hasExistingEmailError = fieldErrors.some(([fieldName]) => fieldName === 'EmailAddress');
        fieldErrors = fieldErrors.filter(([fieldName]) => fieldName !== 'EmailAddress');

        const shouldValidateEmailField = getAcquisitionFlow() !== AcquisitionFlow.PqOfOne;
        if (shouldValidateEmailField && !emailValue && hasExistingEmailError) {
            fieldErrors.push(['EmailAddress', 'RequiredField']);
        } else if (shouldValidateEmailField && emailValue && !isValidEmailFormat(emailValue)) {
            fieldErrors.push(['EmailAddress', 'NotValid']);
        } else if (shouldValidateEmailField && emailValue) {
            const shouldValidateEmail = (
                (!isAuthor && !isAdobeMode) 
                || (isAdobeMode && useEmailApi)
            );
            const isThirdPartyError: boolean = shouldValidateEmail
                && !(await emailValidationApi(emailValue));

            if (isThirdPartyError || hasExistingEmailError) {
                fieldErrors.push(['EmailAddress', 'ThirdParty']);
            }
        }
        // EMAIL VALIDATION END

        // ANCILLARY PRODUCTS VALIDATION START
        if (!checkIsCreditProtectionValid()) {
            fieldErrors.push(['CreditProtectionInitials', 'RequiredField']);
        }
        if (!isAuthorisedUserSectionValid) {
            const dobDate = `${authorizedUserInfo.AuthorizedUserDOBMonth}/${authorizedUserInfo.AuthorizedUserDOBDay}/${authorizedUserInfo.AuthorizedUserDOBYear}`;
            const isSamePerson = await isSamePersonCheck(isSecondPage);

            if (isSamePerson || !acquisitionData?.authorizedUserFirstName?.value) {
                fieldErrors.push(['AuthorizedUserFirstName', 'RequiredField']);
            } else if (acquisitionData?.authorizedUserFirstName.state === 'error') {
                fieldErrors.push(['AuthorizedUserFirstName', 'NotValid']);
            }
            if (isSamePerson || !acquisitionData?.authorizedUserLastName?.value) {
                fieldErrors.push(['AuthorizedUserLastName', 'RequiredField']);
            } else if (acquisitionData?.authorizedUserLastName.state === 'error') {
                fieldErrors.push(['AuthorizedUserLastName', 'NotValid']);
            }
            if (isSamePerson || !isDateValid(dobDate, 'au')) {
                fieldErrors.push([
                    'AuthorizedUserDateOfBirth',
                    'RequiredField',
                ]);
            }
            if (authorizedUserInfo.IsAuthorizedUserSpouse === undefined) {
                fieldErrors.push(['IsAuthorizedUserSpouse', 'RequiredField']);
            }
        }

        // ANCILLARY PRODUCTS VALIDATION END

        // NEW PROVIDER REPLACEMENT
        // If there is an existing error for the key, remove it, and assert the Field Error.
        // Refer to modernReadManager.tsx for similar implementation
        const validationMigrationMap: Partial<Record<IFieldName, keyof typeof acquisitionData>> = {};

        if (getFeatureFlags('DPM-13102')) {
            validationMigrationMap.FirstName = 'firstName';
            validationMigrationMap.LastName = 'lastName';
            validationMigrationMap.NameSuffix = 'suffix';
            // --- Always Disabled / Optional ---
            // validationMigrationMap.MiddleInitial = 'middleInitial';
        }

        if (getFeatureFlags('DPM-13566')) {
            validationMigrationMap.EmailAddress = 'emailAddress';
        }

        if (getFeatureFlags('DPM-14217')) {
            validationMigrationMap.PrimaryPhoneNumber = 'phoneNumber';
        }

        for (const [key, fieldName] of Object.entries(validationMigrationMap)) {
            const keyFieldName = key as IFieldName;
            const hasExistingFieldError = fieldErrors.some(([existingFieldName]) => existingFieldName === keyFieldName);
            fieldErrors = fieldErrors.filter(([existingFieldName]) => existingFieldName !== keyFieldName);

            const field = acquisitionData[fieldName];
            if (field?.state === 'error' && (keyFieldName === 'EmailAddress' || hasExistingFieldError)) {
                let errorType: TErrorType = 'RequiredField';
                if (keyFieldName === 'EmailAddress' || keyFieldName === 'FirstName' || keyFieldName === 'LastName' || keyFieldName === 'PrimaryPhoneNumber') {
                    switch (field.errorType) {
                        case 'invalidInput': errorType = 'NotValid'; break;
                        case 'thirdParty': errorType = 'ThirdParty'; break;
                        case 'notEligible': errorType = 'NotEligible'; break;
                        case 'requiredField':
                        case 'None':
                        default: errorType = 'RequiredField'; break;
                    }
                }
                fieldErrors.push([keyFieldName, errorType]);
            }
        }

        // NEW PROVIDER REPLACEMENT END
        if (fieldErrors.length > 0) {
            return showPushdown({
                header: 'Problem Processing Your Request.',
                description:
                    'The following fields and/or sections require your attention:',
                message: [],
                fieldErrors: sortPushdownErrors(fieldErrors),
                monthlyIncome: acquisitionData.monthlyIncome.value || '',
                color: 'error',
            }, homeAddressPrimary);
        }
        if (!isEsignLinkValid && isEsignShowing) {
            return showPushdown('processingError');
        }
    };

    return {
        getInvalidPersonalInformationFields,
        handleErrorPushdown,
    };
};

export default useHandleErrorPushdown;
